/**
 * Staff self-registration (salesperson / driver).
 *
 * Reached from the "Who are you?" chooser on the registration screen, after the
 * mobile number has been verified by OTP. Collects the core enrolment details —
 * the same set the admin's Add-salesperson/Add-driver form asks for, minus the
 * document uploads, which are gathered after approval. All core fields are
 * required; the submission creates the account in "awaiting approval" and gives
 * no access until an admin approves it.
 *
 * Documents (photo, Aadhaar, PAN copy, licence, cancelled cheque) are
 * deliberately NOT here: the person has no token yet, so there is nothing to
 * upload against. The admin collects them at/after approval.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { Banner, Button, Card, TextInput } from '../../components/ui';
import { phoneInputDigits } from '../../lib/phone';

const RX = {
  mobile: /^[6-9]\d{9}$/,
  pan: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
  ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/,
  pin6: /^\d{6}$/,
  acc: /^\d{9,18}$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

export default function StaffRegister({
  role, phone, ticket, onBack,
}: {
  role: 'salesperson' | 'driver';
  phone: string;
  ticket?: string;
  onBack: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const isDriver = role === 'driver';

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  // Personal
  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [email, setEmail] = useState('');
  const [altPhone, setAltPhone] = useState('');
  // Family / next of kin
  const [fatherName, setFatherName] = useState('');
  const [fatherPhone, setFatherPhone] = useState('');
  const [nokName, setNokName] = useState('');
  const [nokRel, setNokRel] = useState('');
  const [nokPhone, setNokPhone] = useState('');
  // KYC
  const [pan, setPan] = useState('');
  const [licence, setLicence] = useState('');
  const [licenceExpiry, setLicenceExpiry] = useState('');
  // Address
  const [permAddr, setPermAddr] = useState('');
  const [permPin, setPermPin] = useState('');
  const [curAddr, setCurAddr] = useState('');
  const [curPin, setCurPin] = useState('');
  // Bank
  const [acHolder, setAcHolder] = useState('');
  const [bankBranch, setBankBranch] = useState('');
  const [acNumber, setAcNumber] = useState('');
  const [ifsc, setIfsc] = useState('');

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (fullName.trim().length < 2) e.fullName = 'Enter the full name.';
    if (!dob) e.dateOfBirth = 'Date of birth is required.';
    if (email && !RX.email.test(email)) e.email = 'Enter a valid email address.';
    if (altPhone && !RX.mobile.test(altPhone)) e.altPhone = '10 digits, starting 6–9.';
    if (fatherName.trim().length < 2) e.fatherName = "Enter the father's name.";
    if (fatherPhone && !RX.mobile.test(fatherPhone)) e.fatherPhone = '10 digits, starting 6–9.';
    if (nokName.trim().length < 2) e.nokName = 'Enter the next-of-kin name.';
    if (nokRel.trim().length < 2) e.nokRelationship = 'Enter the relationship.';
    if (!RX.mobile.test(nokPhone)) e.nokPhone = 'Enter a 10-digit mobile number.';
    if (!RX.pan.test(pan)) e.panNumber = 'Format: ABCDE1234F.';
    if (isDriver) {
      if (licence.trim().length < 4) e.licenceNumber = 'Enter the driving licence number.';
      if (!licenceExpiry) e.licenceExpiry = 'Enter the licence validity date.';
    }
    if (permAddr.trim().length < 5) e.permanentAddress = 'Enter the permanent address.';
    if (!RX.pin6.test(permPin)) e.permanentPincode = 'PIN code is 6 digits.';
    if (curPin && !RX.pin6.test(curPin)) e.currentPincode = 'PIN code is 6 digits.';
    if (acHolder.trim().length < 2) e.bankAccountHolder = 'Enter the account holder name.';
    if (bankBranch.trim().length < 2) e.bankNameBranch = 'Enter the bank name & branch.';
    if (!RX.acc.test(acNumber)) e.bankAccountNumber = 'Account number is 9–18 digits.';
    if (!RX.ifsc.test(ifsc)) e.bankIfsc = 'Format: SBIN0001234.';
    setFieldErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    setError('');
    if (!validate()) return;
    setBusy(true);
    try {
      await api.post('/api/staff/register', {
        registrationTicket: ticket,
        role,
        fullName: fullName.trim(),
        dateOfBirth: dob,
        email: email.trim() || undefined,
        altPhone: altPhone.replace(/\D/g, '') || undefined,
        fatherName: fatherName.trim(),
        fatherPhone: fatherPhone.replace(/\D/g, '') || undefined,
        nokName: nokName.trim(),
        nokRelationship: nokRel.trim(),
        nokPhone: nokPhone.replace(/\D/g, ''),
        panNumber: pan.trim().toUpperCase(),
        licenceNumber: isDriver ? licence.trim() : undefined,
        licenceExpiry: isDriver ? licenceExpiry : undefined,
        permanentAddress: permAddr.trim(),
        permanentPincode: permPin,
        currentAddress: curAddr.trim() || undefined,
        currentPincode: curPin || undefined,
        bankAccountHolder: acHolder.trim(),
        bankNameBranch: bankBranch.trim(),
        bankAccountNumber: acNumber,
        bankIfsc: ifsc.trim().toUpperCase(),
      }, { anonymous: true });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.fields) setFieldErrors(err.fields);
      } else {
        setError(t('app.somethingWrong'));
      }
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <div className="app">
        <main className="screen center" style={{ maxWidth: 420 }}>
          <div className="stack-4 t-center">
            <div
              style={{
                width: 56, height: 56, borderRadius: '50%', background: 'var(--ok-bg)',
                color: 'var(--ok)', display: 'grid', placeItems: 'center', margin: '0 auto',
                fontSize: 26, fontWeight: 700,
              }}
              aria-hidden
            >✓</div>
            <h1>{t('auth.pendingApproval')}</h1>
            <p className="t-sm t-muted">{t('auth.pendingApprovalHint')}</p>
            <Button variant="ghost" block onClick={() => navigate('/login', { replace: true })}>
              {t('app.done')}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const roleLabel = isDriver ? 'Driver' : 'Salesperson';

  return (
    <div className="app">
      <main className="screen" style={{ maxWidth: 560 }}>
        <div className="stack-4" style={{ paddingTop: 24 }}>
          <div className="stack-2">
            <h1>{roleLabel} registration</h1>
            <p className="t-sm t-muted">{phone}</p>
          </div>

          <Banner tone="info" title="Your details go to the office for approval">
            <span className="t-sm">
              Fill in your details below. They are sent to the admin for approval — you
              will be able to sign in and start work once approved. Documents (photo,
              Aadhaar, PAN, {isDriver ? 'licence, ' : ''}cancelled cheque) will be collected
              after approval.
            </span>
          </Banner>

          {!ticket && (
            <Banner tone="warn" title="Start from the login screen">
              <span className="t-sm">
                This page needs a freshly verified mobile number. Go back, enter your
                number and the code you receive, and you will be brought straight here.
              </span>
            </Banner>
          )}

          {error && <Banner tone="danger">{error}</Banner>}

          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h3>Personal details</h3>
              <TextInput
                label="Full name" required value={fullName}
                error={fieldErrors.fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
              <TextInput
                label="Date of birth" type="date" required value={dob}
                error={fieldErrors.dateOfBirth}
                onChange={(e) => setDob(e.target.value)}
              />
              <TextInput
                label="Email" value={email} error={fieldErrors.email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextInput
                label="Alternate mobile" inputMode="numeric" value={altPhone}
                error={fieldErrors.altPhone}
                onChange={(e) => setAltPhone(phoneInputDigits(e.target.value))}
              />
            </div>
          </Card>

          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h3>Family &amp; next of kin</h3>
              <TextInput
                label="Father's name" required value={fatherName}
                error={fieldErrors.fatherName}
                onChange={(e) => setFatherName(e.target.value)}
              />
              <TextInput
                label="Father's mobile" inputMode="numeric" value={fatherPhone}
                error={fieldErrors.fatherPhone}
                onChange={(e) => setFatherPhone(phoneInputDigits(e.target.value))}
              />
              <TextInput
                label="Next of kin name" required value={nokName}
                error={fieldErrors.nokName}
                onChange={(e) => setNokName(e.target.value)}
              />
              <TextInput
                label="Relationship" required value={nokRel}
                error={fieldErrors.nokRelationship}
                onChange={(e) => setNokRel(e.target.value)}
              />
              <TextInput
                label="Next of kin mobile" required inputMode="numeric" value={nokPhone}
                error={fieldErrors.nokPhone}
                onChange={(e) => setNokPhone(phoneInputDigits(e.target.value))}
              />
            </div>
          </Card>

          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h3>Identity</h3>
              <TextInput
                label="PAN" required value={pan} maxLength={10}
                error={fieldErrors.panNumber}
                onChange={(e) => setPan(e.target.value.toUpperCase())}
                style={{ textTransform: 'uppercase' }}
              />
              {isDriver && (
                <>
                  <TextInput
                    label="Driving licence number" required value={licence} maxLength={40}
                    error={fieldErrors.licenceNumber}
                    onChange={(e) => setLicence(e.target.value)}
                  />
                  <TextInput
                    label="Licence valid until" type="date" required value={licenceExpiry}
                    error={fieldErrors.licenceExpiry}
                    onChange={(e) => setLicenceExpiry(e.target.value)}
                  />
                </>
              )}
            </div>
          </Card>

          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h3>Address</h3>
              <TextInput
                label="Permanent address" required value={permAddr}
                error={fieldErrors.permanentAddress}
                onChange={(e) => setPermAddr(e.target.value)}
              />
              <TextInput
                label="Permanent PIN code" numeric maxLength={6} required value={permPin}
                error={fieldErrors.permanentPincode}
                onChange={(e) => setPermPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              <TextInput
                label="Current address (if different)" value={curAddr}
                onChange={(e) => setCurAddr(e.target.value)}
              />
              <TextInput
                label="Current PIN code" numeric maxLength={6} value={curPin}
                error={fieldErrors.currentPincode}
                onChange={(e) => setCurPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>
          </Card>

          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h3>Bank account</h3>
              <TextInput
                label="Account holder name" required value={acHolder}
                error={fieldErrors.bankAccountHolder}
                onChange={(e) => setAcHolder(e.target.value)}
              />
              <TextInput
                label="Bank name & branch" required value={bankBranch}
                error={fieldErrors.bankNameBranch}
                onChange={(e) => setBankBranch(e.target.value)}
              />
              <TextInput
                label="Account number" inputMode="numeric" maxLength={18} required value={acNumber}
                error={fieldErrors.bankAccountNumber}
                onChange={(e) => setAcNumber(e.target.value.replace(/\D/g, '').slice(0, 18))}
              />
              <TextInput
                label="IFSC" required value={ifsc} maxLength={11}
                error={fieldErrors.bankIfsc}
                onChange={(e) => setIfsc(e.target.value.toUpperCase())}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
          </Card>

          <Button block size="lg" busy={busy} onClick={() => void submit()}>
            {t('app.submit')}
          </Button>
          <Button variant="ghost" block disabled={busy} onClick={onBack}>
            {t('app.back')}
          </Button>
        </div>
      </main>
    </div>
  );
}
